import { useCallback, useRef, useState } from "react";
import { Upload, FileCheck2, AlertCircle, Loader2, FileText } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type DropzoneState = "idle" | "uploading" | "success" | "error";

export interface FileDropzoneProps {
  /** Tipos MIME / extensiones aceptados (ej. ".pdf,application/pdf") */
  accept?: string;
  /** Tamaño máximo en MB (default: 20) */
  maxSizeMb?: number;
  /** Callback cuando el usuario suelta o selecciona un archivo válido */
  onFileSelected: (file: File) => void | Promise<void>;
  /** Estado controlado externamente — refleja la fase del proceso */
  state?: DropzoneState;
  /** Nombre del archivo actualmente procesado (controlado externamente) */
  fileName?: string;
  /** Mensaje a mostrar en estado success */
  successMessage?: string;
  /** Mensaje a mostrar en estado error */
  errorMessage?: string;
  /** Texto principal del estado idle */
  idleTitle?: string;
  /** Texto secundario del estado idle */
  idleSubtitle?: string;
  /** Pista de formatos aceptados */
  hint?: string;
  /** Callback de reset (vuelve a idle) */
  onReset?: () => void;
  /** Texto del botón de reset */
  resetLabel?: string;
  /** Deshabilitar la zona de drop */
  disabled?: boolean;
  className?: string;
}

/**
 * FileDropzone — Componente genérico de carga de archivos con drag & drop.
 * 100% nativo HTML5, sin dependencias de terceros.
 *
 * Estados: idle | uploading | success | error (controlados externamente).
 */
export function FileDropzone({
  accept = "*",
  maxSizeMb = 20,
  onFileSelected,
  state = "idle",
  fileName = "",
  successMessage = "Archivo procesado",
  errorMessage = "Error al procesar el archivo",
  idleTitle = "Arrastra tu archivo aquí",
  idleSubtitle = "o haz clic para seleccionar",
  hint,
  onReset,
  resetLabel = "Subir otro archivo",
  disabled = false,
  className,
}: FileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const validateAndDispatch = useCallback(
    (file: File) => {
      setLocalError(null);
      const sizeMb = file.size / (1024 * 1024);
      if (sizeMb > maxSizeMb) {
        setLocalError(`El archivo supera el límite de ${maxSizeMb}MB`);
        return;
      }
      void onFileSelected(file);
    },
    [maxSizeMb, onFileSelected]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (disabled) return;
      const file = e.dataTransfer.files[0];
      if (file) validateAndDispatch(file);
    },
    [disabled, validateAndDispatch]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) validateAndDispatch(file);
    e.target.value = "";
  };

  // ── IDLE ──
  if (state === "idle") {
    return (
      <div className={className}>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !disabled && inputRef.current?.click()}
          className={cn(
            "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
            disabled
              ? "opacity-50 cursor-not-allowed border-muted-foreground/20"
              : "cursor-pointer",
            !disabled && dragOver
              ? "border-primary bg-primary/5"
              : !disabled && "border-muted-foreground/25 hover:border-primary/50"
          )}
        >
          <Upload className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">{idleTitle}</p>
          <p className="text-xs text-muted-foreground mt-1">{idleSubtitle}</p>
          {hint && <p className="text-xs text-muted-foreground mt-3">{hint}</p>}
          {localError && (
            <p className="text-xs text-destructive mt-3 font-medium">{localError}</p>
          )}
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            className="hidden"
            onChange={handleChange}
            disabled={disabled}
          />
        </div>
      </div>
    );
  }

  // ── UPLOADING ──
  if (state === "uploading") {
    return (
      <div className={cn("space-y-4 py-4", className)}>
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 text-primary animate-spin" />
          <span className="text-sm text-foreground font-medium truncate">
            {fileName || "Procesando..."}
          </span>
        </div>
        <Progress value={undefined} className="h-2" />
      </div>
    );
  }

  // ── SUCCESS ──
  if (state === "success") {
    return (
      <div className={cn("space-y-4 py-4", className)}>
        <div className="flex items-center gap-3">
          <FileCheck2 className="h-5 w-5 text-emerald-500 dark:text-emerald-400" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{fileName}</p>
            <p className="text-xs text-muted-foreground">{successMessage}</p>
          </div>
        </div>
        {onReset && (
          <Button variant="outline" size="sm" onClick={onReset} className="w-full">
            {resetLabel}
          </Button>
        )}
      </div>
    );
  }

  // ── ERROR ──
  return (
    <div className={cn("space-y-4 py-4", className)}>
      <div className="flex items-center gap-3">
        <AlertCircle className="h-5 w-5 text-destructive" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">
            {fileName || "Archivo"}
          </p>
          <p className="text-xs text-destructive">{errorMessage}</p>
        </div>
      </div>
      {onReset && (
        <Button variant="outline" size="sm" onClick={onReset} className="w-full">
          Reintentar
        </Button>
      )}
    </div>
  );
}

// Re-export del icono de archivo para conveniencia
export { FileText };
